'use client';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { CompleteMatchesModalSkeleton } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/CompleteMatchesModalSkeleton';
import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { matchupStandingSettingOptions, MatchupWinnerState, WinnerDeterminationInfo } from '@/entities/Matchup';
import {
	getMatchupStatusApiValue,
	MatchCompletedEnum,
	MatchStatusApiMap,
	MatchStatusEnum,
	MatchupCompletedEnumTypes,
	MatchupStatusEnum
} from '@/entities/matchup/constants';
import { Match } from '@/entities/matchup/Matchup';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { useMatchupWinnerDetermination } from '@/hooks/matchup/useMatchupWinnerDetermination';
import { gameSequenceKeys } from '@/hooks/tie-breaker/gameSequenceKeys';
import { formatTeamLeagueLabels, getMatchupWinnerState } from '@/utils/helpers';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	matchupId: string;
	incompleteMatches: Match[];
	tieBreakerMatchId?: string;
	tieBreakerTitle?: string;
	isTieBreakerLineupSet?: boolean;
	tieBreakerLink?: string;
	matchupWinnerDeterminationInfo?: WinnerDeterminationInfo;
	isTieBreakerCancelled: boolean;
}

export const CompleteMatchesModal = ({
	isOpen,
	onClose,
	matchupId,
	incompleteMatches,
	tieBreakerMatchId = '',
	tieBreakerTitle,
	isTieBreakerLineupSet = false,
	tieBreakerLink,
	isTieBreakerCancelled: isTieBreakerCompleted
}: Props) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);
	const queryClient = useQueryClient();

	const modalDataRef = useRef<Match[]>([]);
	const [isInitialized, setIsInitialized] = useState(false);

	const updateMatchupMutation = useUpdateMatchup();
	const updateMatchMutation = useUpdateMatchMutation({
		matchupId: matchupId
	});

	const { data: matchupWinnerDeterminationInfo, refetch, isLoading } = useMatchupWinnerDetermination(matchupId);

	// Initialize snapshot when modal opens
	if (isOpen && !isInitialized) {
		modalDataRef.current = incompleteMatches;
		setIsInitialized(true);
		refetch();
	}

	// Reset when modal closes
	if (!isOpen && isInitialized) {
		setIsInitialized(false);
	}
	const matchupWinnerState = getMatchupWinnerState(incompleteMatches.length, matchupWinnerDeterminationInfo?.data, isTieBreakerCompleted);

	const tieBreakerNeedsToBePlayed = matchupWinnerState === MatchupWinnerState.TIEBREAKER_NEEDS_TO_BE_PLAYED;

	const handleCompleteMatches = async () => {
		const snapshot = modalDataRef.current;

		try {
			// Update all incomplete matches in parallel and wait for all to complete
			await Promise.all(
				snapshot.map((match) => {
					return updateMatchMutation.mutateAsync({
						matchId: match.matchUuid,
						updateData: {
							matchStatus: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
							matchCompletedType: MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
						}
					});
				})
			);

			if (tieBreakerNeedsToBePlayed && tieBreakerMatchId) {
				await updateMatchMutation.mutateAsync(
					{
						matchId: tieBreakerMatchId,
						updateData: {
							matchStatus: MatchStatusApiMap[MatchStatusEnum.IN_PROGRESS_MATCH_STATUS],
							matchCompletedType: MatchCompletedEnum.UNDEFINED_MATCH_COMPLETION_TYPE
						}
					},
					{
						onSuccess: () => {
							queryClient.invalidateQueries({ queryKey: gameSequenceKeys.gameSequenceInfo(tieBreakerMatchId) });
						}
					}
				);
				toast.success('All matches completed and tiebreaker started successfully!');
			} else {
				// After all matches are successfully updated, update the matchup status
				await updateMatchupMutation.mutateAsync({
					matchup_uuid: matchupId,
					matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS),
					matchup_completed_type: MatchupCompletedEnumTypes.PLAYED_MATCHUP_COMPLETION_TYPE
				});

				toast.success('All matches completed successfully!');
			}
			onClose();
		} catch (_error) {
			toast.error('Failed to complete matches. Please try again.');
			onClose();
		}
	};

	if (!isOpen) return null;

	const isPending = updateMatchMutation.isPending || updateMatchupMutation.isPending;
	const displayMatches = modalDataRef.current;

	const matchupStandingSettingLabelMap = Object.fromEntries(matchupStandingSettingOptions.map((opt) => [opt.value, opt.label]));

	return (
		<ModalWrapper
			title={tieBreakerNeedsToBePlayed ? `Activate ${tieBreakerTitle ?? 'Tie Breaker'}` : 'Complete Matches'}
			onClose={onClose}
			className="max-w-[500px]"
			closeOnOutsideClick={!isPending}
		>
			<hr className="text-gray-400 my-4" />
			{isLoading ? (
				<div>
					<CompleteMatchesModalSkeleton />
				</div>
			) : (
				<div className="flex flex-col gap-4 px-2">
					<div className="flex flex-col">
						{matchupWinnerState === MatchupWinnerState.WINNER_DETERMINED_ALL_MATCHES_PLAYED ? (
							<div>
								<p className="text-base font-medium text-gray-900">
									Are you sure you want to complete this {labels.matchupSingularTitle}?
								</p>
								<div className="flex flex-col gap-3">
									<p className="text-sm text-gray-600">
										All matches have been played. The {labels.matchupSingularTitle} will be marked as completed.
									</p>
								</div>
							</div>
						) : null}
						{matchupWinnerState === MatchupWinnerState.WINNER_DETERMINED_NOT_ALL_MATCHES_PLAYED ? (
							<div>
								<p className="text-base font-medium text-gray-900">
									Are you sure you want to complete this {labels.matchupSingularTitle}?
									<span>
										{' '}
										{`The following ${displayMatches.length} match${
											displayMatches.length > 1 ? 'es' : ''
										} ha${displayMatches.length > 1 ? 've' : 's'} not been played:`}
									</span>
								</p>
								<div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
									{displayMatches.map((match) => (
										<div
											key={match.matchUuid}
											className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
										>
											<div className="flex flex-col">
												<span className="font-medium text-gray-900">
													{match.playerGroupTitle}{' '}
													{match.formatTitle !== 'No title for this match' ? `- ${match.formatTitle}` : ''}
												</span>
												{match.roundText ? <span className="text-sm text-gray-600">{match.roundText}</span> : null}
											</div>
											<span className="text-sm text-orange-600 font-medium">Not played</span>
										</div>
									))}
								</div>
								<p className="text-sm text-gray-600">
									All incomplete matches will be canceled and the {labels.matchupSingularTitle} will be completed
								</p>
							</div>
						) : null}
						{tieBreakerNeedsToBePlayed ? (
							<div>
								{isTieBreakerLineupSet ? (
									<div className="text-sm">
										<p className="font-semibold">Are you sure you want to complete this {labels.matchupSingularTitle}?</p>
										<p className="font-semibold">{tieBreakerTitle} match needs to be played.</p>
										<p className="text-gray-600 mt-3">
											All incomplete matches will be marked as canceled and the {labels.matchupSingularTitle} will be completed.
										</p>
									</div>
								) : (
									<div className="pb-2">
										<div>
											<p className="text-sm text-gray-600">
												All incomplete matches will be marked as canceled and the {labels.matchupSingularTitle} will be
												completed.
											</p>
											<div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
												<p className="text-sm font-medium text-orange-800">
													Warning: Lineup not set for {tieBreakerTitle ?? 'tie-breaker'}
												</p>
												<p className="text-xs text-orange-600 mt-1">
													Both teams need to set their lineup before activating the {tieBreakerTitle ?? 'tie-breaker'}{' '}
													match.
												</p>
												{tieBreakerLink ? (
													<div className="mt-2">
														<Link
															href={tieBreakerLink}
															onClick={onClose}
															className="inline-flex items-center text-xs font-medium text-orange-700 hover:text-orange-800 underline"
														>
															Set up {tieBreakerTitle ?? 'tie-breaker'} lineup →
														</Link>
													</div>
												) : null}
											</div>
										</div>
									</div>
								)}
							</div>
						) : null}
						{matchupWinnerState === MatchupWinnerState.TIED_SETTING_DETERMINES_WINNER ? (
							<div>
								<p className="text-base font-medium text-gray-900">Are you sure you want to complete all matches?</p>
								<div className="flex flex-col gap-3">
									<p className="text-sm text-gray-600">
										Your {labels.matchupSingularTitle} will be completed without playing tiebreaker.
									</p>
									<div className="flex flex-col gap-3 bg-gray-100 p-3 rounded-lg">
										<p className="text-sm text-gray-600">
											Selected rule:{' '}
											<span className="font-semibold text-black">
												{(matchupWinnerDeterminationInfo?.data?.tiebreakerSetting &&
													matchupStandingSettingLabelMap[matchupWinnerDeterminationInfo?.data?.tiebreakerSetting]) ??
													''}
											</span>
										</p>
										<p className="text-sm text-gray-600">
											Winner team:{' '}
											<span className="font-semibold text-black">{matchupWinnerDeterminationInfo?.data?.winnerTeamName}</span>
										</p>
									</div>
									<div>
										<p className="text-sm text-gray-600">
											All incomplete matches will be marked as canceled and the {labels.matchupSingularTitle} will be completed.
										</p>
									</div>
								</div>
							</div>
						) : null}
					</div>
					<ActionButtons
						className="flex justify-end gap-3 pt-4"
						closeButtonClassName="w-32"
						submitButtonClassName=" w-fit"
						onClose={() => {
							if (!isPending) {
								onClose();
							}
						}}
						onSubmit={handleCompleteMatches}
						submitButtonText={
							isPending ? 'Completing...' : tieBreakerNeedsToBePlayed ? `Play ${tieBreakerTitle ?? 'Tie Breaker'}` : 'Complete'
						}
						closeButtonText="Cancel"
						submitButtonDisabled={isPending || (tieBreakerNeedsToBePlayed && !isTieBreakerLineupSet)}
					/>
				</div>
			)}
		</ModalWrapper>
	);
};
